import { BlockEditorData } from "@babackapp/blocks-editor/types";
import { isBlockTranslated } from "@babackapp/editor-blocks-library/blocks";
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { EmailTemplateFormData } from "../types";
import { AVAILABLE_LOCALES } from "@babackapp/ui/components/translatable-field";
import { useTemplatesEditorConfig } from "./templates-editor-config-context";

/** BCP-47 (`fr-FR`) → short ISO code (`fr`), mirrors getLocale in the renderer. */
const normalizeLocale = (locale?: string | null): string =>
  locale?.split("-")[0]?.toLowerCase() ?? "";


export type LocaleTranslationState = "empty" | "full" | "partial";

export type TemplateEditorLocaleObject = {
  value: string;
  label: string;
  flag: string;
  state: LocaleTranslationState;
}

interface TemplatesEditorLocaleContextType {
  currentLocale: string;
  defaultLocale: string;
  setCurrentLocale: (locale: string) => void;
  availableLocales: TemplateEditorLocaleObject[];
  /** Short locale codes only (e.g. ["fr", "en"]), for consumers that just need
   * the list of codes without the per-locale translation state. */
  localeCodes: string[];
}


const TemplatesEditorLocaleContext = createContext<
  TemplatesEditorLocaleContextType | undefined
>(undefined);


export interface TemplatesEditorLocaleProviderProps {
  children: ReactNode;
}


export function TemplatesEditorLocaleProvider({
  children,
}: TemplatesEditorLocaleProviderProps) {
  const { data: shop } = useCurrentShopSuspense();
  const { domain } = useTemplatesEditorConfig();

  // Resolve the locale list, default locale and initial locale per domain.
  // Memoized so the array reference stays stable across renders (otherwise it
  // would defeat the `value` useMemo below and re-run getLocaleContentState
  // every render).
  //  - Return: customer-portal languages (shop.preferences.languages, BCP-47
  //    e.g. "fr-FR"), normalized to short codes so they match AVAILABLE_LOCALES
  //    and block content. Kept non-empty (falls back to the default locale) so
  //    the switcher, per-block panel and validation are never empty.
  //  - Tracking: left exactly as before — raw trackingPreferences values, no
  //    normalization, initial locale "fr".
  const { availableLocales, defaultLocale, initialLocale } = useMemo(() => {
    if (domain === "return") {
      const codes = [
        ...new Set((shop.preferences?.languages ?? []).map(normalizeLocale)),
      ].filter(Boolean);
      const def = normalizeLocale(shop.preferences?.languages?.[0]) || "en";
      return {
        availableLocales: codes.length ? codes : [def],
        defaultLocale: def,
        initialLocale: def,
      };
    }
    return {
      availableLocales: shop.trackingPreferences?.availableLocales ?? [],
      defaultLocale: shop.trackingPreferences?.notificationsDefaultLocale || "en",
      initialLocale: "fr",
    };
  }, [
    domain,
    shop.preferences?.languages,
    shop.trackingPreferences?.availableLocales,
    shop.trackingPreferences?.notificationsDefaultLocale,
  ]);

  const [currentLocale, setCurrentLocale] = useState<string>(
    () => initialLocale,
  );

  const { control } = useFormContext<EmailTemplateFormData>();
  const content = useWatch({
    control,
    name: "content",
  });
  const subject = useWatch({
    control,
    name: "subject",
  });
  const value: TemplatesEditorLocaleContextType = useMemo(
    () => ({
      currentLocale,
      setCurrentLocale,
      defaultLocale,
      localeCodes: availableLocales,
      availableLocales: availableLocales.map((locale) => ({
        value: locale,
        label:
          AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.label ||
          locale,
        flag: AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES]?.flag,
        state: getLocaleContentState(locale, content, subject as Record<string, string>),
      })),
    }),
    [currentLocale, setCurrentLocale, content, defaultLocale, subject, availableLocales],
  );

  return (
    <TemplatesEditorLocaleContext.Provider value={value}>
      {children}
    </TemplatesEditorLocaleContext.Provider>
  );
}


export function useEmailEditorLocale() {
  const context = useContext(TemplatesEditorLocaleContext);

  if (context === undefined) {
    throw new Error(
      "useEmailEditorLocale must be used within a EmailEditorLocaleProvider",
    );
  }
  return context;
}


function getLocaleContentState(
  locale: string,
  emailContent: BlockEditorData,
  subject: Record<string, string>,
): LocaleTranslationState {
  if (!emailContent?.beforeBlocks || !emailContent?.insideBlocks || !emailContent?.afterBlocks) return "empty";

  const allBlocks = [
    ...emailContent.beforeBlocks,
    ...emailContent.insideBlocks,
    ...emailContent.afterBlocks,
  ];
  const contentBlocks = allBlocks.filter((block) => !!block.content);

  if (contentBlocks.length === 0) {
    return "full";
  }

  const translatedBlocks = contentBlocks.filter((block) => {
    return isBlockTranslated(locale, block) || false;
  });
  const progress = (translatedBlocks.length / contentBlocks.length) * 100;

  if (!subject?.[locale]) {
    return progress === 0 ? "empty" : "partial";
  }

  return progress === 0 ? "empty" : progress >= 100 ? "full" : "partial";
}
